import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { GIButton, Modal } from 'app-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { ModalContextType } from '../../../contexts/modalContext';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import RadioButtons from '../../../lib/inputs/RadioButtons';
import Input from '../../../components/common/form/new/Input';
import Form from '../../../lib/wrappers/Form';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { EMAIL_REGEX } from '../../../utils/validators/regex';
import { ValidationErrorMessage } from '../../../components/common/form';
import { TEXTAREA_MAX_LENGTH } from '../../../constants/enrollments/enrollments.constants';
import { IResetPasswordSubmitEmailRequest } from '../common/types';
import {
    StyledModalBody,
    StyledNoteSection,
    StyledFieldModal as StyledField,
    StyledCharacterCounter,
} from '../ResetPasswordStyles';
import { SEND_EMAIL_MODAL_KEY } from './modalConfig';

interface IModalProps extends ModalContextType {
    onSubmit: (data: IResetPasswordSubmitEmailRequest) => void;
}

interface IFormInput {
    emailType: string;
    email?: string;
    reason?: string;
}

const DefaultValues: IFormInput = {
    emailType: '',
    email: '',
    reason: '',
};

enum EmailType {
    CURRENT_ACCOUNT_EMAIL = 'hixEmail',
    ALTERNATE_EMAIL = 'alternateEmail',
}

const emailTypeOptions = [
    { text: 'resetPassword.sendEmail.modal.email.currentEmail.label', value: EmailType.CURRENT_ACCOUNT_EMAIL },
    { text: 'resetPassword.sendEmail.modal.email.alternateEmail.label', value: EmailType.ALTERNATE_EMAIL },
];

const sendEmailSchema = intl => {
    const baseSchema = z.object({
        emailType: z.nativeEnum(EmailType),
    });

    const alternateEmailSchema = baseSchema.extend({
        email: z
            .string()
            .min(1, intl.formatMessage({ id: 'resetPassword.sendEmail.modal.email.required' }))
            .regex(EMAIL_REGEX, intl.formatMessage({ id: 'resetPassword.sendEmail.modal.email.invalid' })),
        reason: z.string().min(1, intl.formatMessage({ id: 'resetPassword.sendEmail.modal.reason.required' })),
    });

    const currentEmailSchema = baseSchema.extend({
        email: z.string().optional(),
        reason: z.string().optional(),
    });

    return z.union([
        alternateEmailSchema.refine(data => data.emailType === EmailType.ALTERNATE_EMAIL),
        currentEmailSchema.refine(data => data.emailType === EmailType.CURRENT_ACCOUNT_EMAIL),
    ]);
};

const SendEmailModal = ({ closeModal, onSubmit }: IModalProps) => {
    const intl = useIntl();

    const emailOptions = useMemo(() => {
        return emailTypeOptions.map(option => ({
            ...option,
            text: intl.formatMessage({ id: option.text }),
        }));
    }, [intl]);

    const methods = useForm<IFormInput>({
        defaultValues: DefaultValues,
        resolver: zodResolver(sendEmailSchema(intl)),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const {
        register,
        setValue,
        watch,
        handleSubmit,
        reset,
        formState: { errors, isValid },
    } = methods;

    const values = watch();

    const handleClose = useCallback(() => {
        closeModal(SEND_EMAIL_MODAL_KEY);
    }, [closeModal]);

    const handleFieldChange = (name: keyof IFormInput, value: string) => {
        setValue(name, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const remainingChars = useMemo(() => TEXTAREA_MAX_LENGTH - (values.reason?.length || 0), [values.reason]);

    const handleFormSubmit = useCallback(
        (data: IFormInput) => {
            const payload: IResetPasswordSubmitEmailRequest = {
                emailType: data.emailType,
                ...(data.emailType === EmailType.ALTERNATE_EMAIL && {
                    email: data.email,
                    reason: data.reason,
                }),
            };
            onSubmit(payload);
            reset();
        },
        [onSubmit, reset, handleClose],
    );

    return (
        <StyledModalBody>
            <Modal id={SEND_EMAIL_MODAL_KEY} data-testid="alternateEmailModal">
                <Modal.Header
                    id="alternateEmailModalHeader"
                    titleId={'resetPassword.sendEmail.modal.title'}
                    onHide={handleClose}
                    data-testid="modal-header"
                />
                <Modal.Body data-testid="alternateEmailModalBody">
                    <Form
                        formName="resend-transactions-form"
                        data-testid="resend-transactions-form"
                        onSubmit={handleFormSubmit}
                    >
                        <StyledField>
                            <RadioButtons
                                label={{
                                    labelClass: [],
                                    showRequiredIcon: true,
                                    labelText: '',
                                }}
                                inputData={{
                                    id: 'emailType',
                                    isInputRequired: true,
                                    type: 'radio',
                                    options: emailOptions,
                                    value: values.emailType,
                                    ...register('emailType'),
                                }}
                                legend={{
                                    dataInstructions: false,
                                    legendText: intl.formatMessage({
                                        id: 'resetPassword.sendEmail.modal.emailSelect.label',
                                    }),
                                }}
                                inputActions={{
                                    handleFieldChange: e => handleFieldChange('emailType', e),
                                }}
                            />
                        </StyledField>

                        <ConditionalRendering isTrue={values.emailType === EmailType.ALTERNATE_EMAIL}>
                            <StyledField className="ml-2">
                                <Input
                                    label={{
                                        labelText: '',
                                        labelClasses: [],
                                        wrapperClasses: [''],
                                        isRequired: false,
                                    }}
                                    input={{
                                        id: 'email',
                                        type: 'text',
                                        isInputRequired: true,
                                        value: values.email,
                                        ...register('email'),
                                    }}
                                    actions={{
                                        handleFieldChange: value => handleFieldChange('email', value),
                                    }}
                                />
                                <ValidationErrorMessage
                                    isVisible={!!errors.email}
                                    messageText={errors?.email?.message}
                                />
                            </StyledField>

                            <StyledField>
                                <UniversalInput
                                    label={{
                                        text: intl.formatMessage({ id: 'resetPassword.sendEmail.modal.reason.label' }),
                                        labelClasses: ['spl__width--01'],
                                    }}
                                    inputData={{
                                        id: 'reason',
                                        isInputRequired: true,
                                        type: INPUT_TYPES.TEXTAREA,
                                        value: values.reason,
                                        currentData: { reason: values.reason },
                                        liClass: ['text-left'],
                                        ...register('reason'),
                                        rows: 8,
                                    }}
                                    errorClasses={['margin-l-0']}
                                    inputActions={{
                                        onFieldChange: ({ reason }) => {
                                            handleFieldChange('reason', reason);
                                        },
                                    }}
                                    data-testid="alternate-email-input"
                                />
                                <StyledCharacterCounter>
                                    {intl.formatMessage(
                                        { id: 'resetPassword.sendEmail.textCharLeft' },
                                        { remainingChars },
                                    )}
                                </StyledCharacterCounter>
                                <ValidationErrorMessage
                                    isVisible={!!errors.reason}
                                    messageText={errors?.reason?.message}
                                />
                            </StyledField>
                        </ConditionalRendering>

                        <StyledNoteSection>
                            {intl.formatMessage({ id: 'resetPassword.sendEmail.modal.message' })}
                        </StyledNoteSection>
                    </Form>
                </Modal.Body>
                <Modal.Controls>
                    <GIButton
                        id="cancel_btn"
                        labelId="resetPassword.button.cancel"
                        variant="secondary"
                        type="reset"
                        onClickHandler={handleClose}
                    />
                    <GIButton
                        id="submit_btn"
                        labelId="resetPassword.button.submit"
                        isDisabled={!isValid}
                        onClickHandler={handleSubmit(handleFormSubmit)}
                    />
                </Modal.Controls>
            </Modal>
        </StyledModalBody>
    );
};

export default SendEmailModal;
